import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Loader2, X, Check, BarChart3, Plus } from 'lucide-react';

/**
 * Widget que muestra una encuesta inline (pregunta + opciones + resultados).
 * Sirve para CUALQUIER poll publicado en feed_polls (Red Social o Foros de Clase).
 * Solo necesita el poll_id.
 */
export const PollWidget = ({ pollId, initialQuestion, initialOptions, accent = 'purple' }) => {
  const { API, user } = useContext(AuthContext);
  const [results, setResults] = useState(null);
  const [voting, setVoting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchResults = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/stream-feeds/polls/${pollId}/results`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setResults(data);
      }
    } catch (e) {
      console.warn('Error cargando resultados encuesta:', e);
    } finally {
      setLoading(false);
    }
  }, [API, pollId]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const vote = async (optionId) => {
    if (voting || results?.is_closed) return;
    setVoting(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/stream-feeds/polls/${pollId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ option_id: optionId }),
      });
      if (r.ok) {
        toast.success('Voto registrado');
        await fetchResults();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'No se pudo votar');
      }
    } catch (e) {
      toast.error('Error votando');
    } finally {
      setVoting(false);
    }
  };

  const palette = accent === 'indigo'
    ? {
        wrapper: 'from-indigo-50 to-blue-50 border-indigo-200',
        label: 'text-indigo-700',
        myBorder: 'border-indigo-400',
        myBg: 'bg-indigo-200',
        myText: 'text-indigo-700',
      }
    : {
        wrapper: 'from-purple-50 to-blue-50 border-purple-200',
        label: 'text-purple-700',
        myBorder: 'border-purple-400',
        myBg: 'bg-purple-200',
        myText: 'text-purple-700',
      };

  if (loading) {
    return (
      <div className={`mt-3 p-3 bg-gradient-to-br ${palette.wrapper} border rounded-xl animate-pulse`}>
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
        <div className="space-y-2">
          {(initialOptions || []).map((o, i) => (
            <div key={i} className="h-9 bg-white/60 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }
  if (!results) {
    return (
      <div className={`mt-3 p-3 bg-purple-50 border border-purple-200 rounded-xl text-sm ${palette.label}`}>
        <BarChart3 className="w-4 h-4 inline mr-1" /> Encuesta no disponible
      </div>
    );
  }

  const closed = results.is_closed;
  const total = results.total_votes || 0;
  const closesDate = results.closes_at ? new Date(results.closes_at) : null;
  const safeOptions = Array.isArray(results.options) ? results.options : [];
  // Audiencia dirigida: si el backend marca can_vote=false, esta encuesta no
  // está dirigida al usuario actual. Puede ver opciones+resultados pero no votar.
  const canVote = results.can_vote !== false;
  const hasTargeting = Array.isArray(results.target_user_ids) && results.target_user_ids.length > 0;
  // El creador (o admin) puede editar y siempre votar
  const puedeEditar = !!results.es_creador || user?.rol === 'admin';

  return (
    <div className={`mt-3 p-4 bg-gradient-to-br ${palette.wrapper} border rounded-xl`} data-testid={`poll-widget-${pollId}`}>
      <div className={`flex items-center gap-2 mb-3 ${palette.label}`}>
        <BarChart3 className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">Encuesta</span>
        {closed && <span className="ml-auto text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">Cerrada</span>}
        {puedeEditar && !closed && (
          <button
            type="button"
            onClick={() => setShowEditModal(true)}
            className={`${closed ? '' : 'ml-auto'} text-xs px-2 py-0.5 bg-white/80 hover:bg-white border border-current rounded-full ${palette.label} font-semibold transition`}
            data-testid={`poll-edit-btn-${pollId}`}
            title="Editar encuesta"
          >
            ✏️ Editar
          </button>
        )}
      </div>
      <p className="font-semibold text-gray-800 mb-3">{results.question || initialQuestion}</p>
      {hasTargeting && (
        <div className={`mb-3 px-3 py-2 rounded-lg text-xs ${canVote ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
          {canVote
            ? '🎯 Esta encuesta está dirigida a vos. Tu voto cuenta.'
            : '🔒 Esta encuesta está dirigida a otras personas. Podés verla pero no votar.'}
        </div>
      )}
      <div className="space-y-2">
        {safeOptions.map((o) => {
          if (!o || !o.id) return null;
          const isMyVote = results.your_vote === o.id;
          const showResults = !!results.your_vote || closed || !canVote;
          return (
            <button
              key={o.id}
              type="button"
              disabled={voting || closed || !canVote}
              onClick={() => vote(o.id)}
              className={`relative w-full text-left p-2.5 rounded-lg border transition-all overflow-hidden ${
                isMyVote
                  ? `${palette.myBorder} bg-white shadow-sm`
                  : (closed || !canVote)
                  ? 'border-gray-200 bg-white/70 cursor-not-allowed'
                  : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm cursor-pointer'
              }`}
              data-testid={`poll-option-${o.id}`}
            >
              {showResults && (
                <div
                  className={`absolute inset-y-0 left-0 ${isMyVote ? palette.myBg : 'bg-gray-100'} transition-all duration-500`}
                  style={{ width: `${o.percent || 0}%` }}
                />
              )}
              <div className="relative flex items-center justify-between gap-2">
                <span className={`font-medium ${isMyVote ? palette.myText : 'text-gray-800'} flex-1 text-sm`}>
                  {isMyVote && <Check className="w-3.5 h-3.5 inline mr-1" />}
                  {o.text}
                </span>
                {showResults && (
                  <span className="text-xs font-semibold text-gray-600 shrink-0">
                    {o.percent || 0}% · {o.count}
                  </span>
                )}
              </div>
              {results.show_voters && Array.isArray(o.voters) && o.voters.length > 0 && showResults && (
                <div className="relative mt-1 text-[10px] text-gray-500 truncate">
                  {o.voters.map(v => v?.name || 'Anónimo').join(', ')}
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>{total} {total === 1 ? 'voto' : 'votos'}</span>
        {closesDate && !closed && (
          <span>Cierra: {closesDate.toLocaleDateString('es-UY')} {closesDate.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })}</span>
        )}
      </div>
      {showEditModal && (
        <EditPollModal
          pollId={pollId}
          current={results}
          API={API}
          onClose={() => setShowEditModal(false)}
          onSaved={async () => { setShowEditModal(false); await fetchResults(); }}
        />
      )}
    </div>
  );
};

/**
 * Modal de edición de encuesta. Permite cambiar pregunta, opciones, y fecha de
 * cierre. Si se modifican las opciones, los votos existentes se descartan
 * (el backend devuelve `votes_dropped:true` y mostramos aviso).
 */
const EditPollModal = ({ pollId, current, API, onClose, onSaved }) => {
  const [question, setQuestion] = useState(current?.question || '');
  const [options, setOptions] = useState(
    Array.isArray(current?.options) && current.options.length > 0
      ? current.options.map(o => ({ id: o.id, label: o.text || o.label || '' }))
      : [{ id: '', label: '' }, { id: '', label: '' }]
  );
  const [closesAt, setClosesAt] = useState(
    current?.closes_at ? new Date(current.closes_at).toISOString().slice(0, 16) : ''
  );
  const [submitting, setSubmitting] = useState(false);

  const updateOption = (i, label) => {
    setOptions(prev => prev.map((o, idx) => idx === i ? { ...o, label } : o));
  };
  const removeOption = (i) => {
    if (options.length <= 2) return;
    setOptions(prev => prev.filter((_, idx) => idx !== i));
  };
  const addOption = () => {
    if (options.length >= 8) return;
    setOptions(prev => [...prev, { id: '', label: '' }]);
  };

  const handleSubmit = async () => {
    const q = question.trim();
    const cleanOptions = options.map(o => ({ id: o.id || undefined, label: (o.label || '').trim() })).filter(o => o.label);
    if (q.length < 3) { toast.error('La pregunta debe tener al menos 3 caracteres'); return; }
    if (cleanOptions.length < 2) { toast.error('Debe haber al menos 2 opciones'); return; }

    // Verificar si cambiaron opciones para avisar al usuario
    const oldLabels = (current?.options || []).map(o => (o.text || o.label || '').trim()).sort();
    const newLabels = cleanOptions.map(o => o.label).sort();
    const optionsChanged = JSON.stringify(oldLabels) !== JSON.stringify(newLabels);
    if (optionsChanged && (current?.total_votes || 0) > 0) {
      if (!window.confirm(`Cambiar las opciones descartará los ${current.total_votes} voto(s) existentes. ¿Continuar?`)) return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const body = { question: q, options: cleanOptions };
      if (closesAt) {
        body.closes_at = new Date(closesAt).toISOString();
      } else {
        body.closes_at = null;
      }
      const r = await fetch(`${API}/stream-feeds/polls/${pollId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(data.votes_dropped ? 'Encuesta editada. Votos previos descartados.' : 'Encuesta actualizada');
        onSaved && onSaved();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'No se pudo editar');
      }
    } catch (e) {
      toast.error('Error editando');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-poll-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Editar encuesta</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <label className="block text-sm font-medium text-gray-700 mb-1">Pregunta</label>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 mb-4"
          data-testid="edit-poll-question"
        />

        <label className="block text-sm font-medium text-gray-700 mb-1">Opciones</label>
        <div className="space-y-2 mb-3">
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={o.label}
                onChange={(e) => updateOption(i, e.target.value)}
                placeholder={`Opción ${i + 1}`}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                data-testid={`edit-poll-opt-${i}`}
              />
              {options.length > 2 && (
                <button onClick={() => removeOption(i)} className="text-gray-400 hover:text-red-500 p-1"><X className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
        {options.length < 8 && (
          <button onClick={addOption} className="text-sm text-purple-600 hover:underline mb-4 flex items-center gap-1">
            <Plus className="w-4 h-4" /> Agregar opción
          </button>
        )}

        <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">Fecha de cierre (opcional)</label>
        <input
          type="datetime-local"
          value={closesAt}
          onChange={(e) => setClosesAt(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 mb-4"
          data-testid="edit-poll-closes-at"
        />
        {closesAt && (
          <button onClick={() => setClosesAt('')} className="text-xs text-gray-500 hover:underline -mt-3 mb-3">Sin fecha de cierre</button>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg" data-testid="edit-poll-cancel">Cancelar</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || question.trim().length < 3}
            className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-lg flex items-center gap-2"
            data-testid="edit-poll-submit"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Modal genérico para crear encuesta. Acepta `endpoint` para soportar:
 *  - Red Social Global: POST /api/stream-feeds/polls
 *  - Foros de Clase:   POST /api/foros-clase/{claseId}/polls
 */
export const CreatePollModal = ({ isOpen, onClose, onCreated, API, endpoint = '/stream-feeds/polls', accent = 'purple', title = 'Crear Encuesta', audienceOptions = null }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [closesAt, setClosesAt] = useState('');
  const [showVoters, setShowVoters] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Audiencia dirigida (opcional). Si audienceOptions === null, no se muestra el bloque.
  // audienceOptions es un array [{id, name, role}] de personas que el creador
  // puede seleccionar como destinatarios.
  const [audienceEnabled, setAudienceEnabled] = useState(false);
  const [audienceSelected, setAudienceSelected] = useState(new Set());

  const reset = () => {
    setQuestion('');
    setOptions(['', '']);
    setClosesAt('');
    setShowVoters(false);
    setAudienceEnabled(false);
    setAudienceSelected(new Set());
  };

  const updateOption = (i, val) => {
    setOptions(prev => prev.map((o, idx) => (idx === i ? val : o)));
  };
  const addOption = () => {
    if (options.length < 6) setOptions(prev => [...prev, '']);
  };
  const removeOption = (i) => {
    if (options.length > 2) setOptions(prev => prev.filter((_, idx) => idx !== i));
  };
  const toggleAudience = (id) => {
    setAudienceSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    const cleanOptions = options.map(o => o.trim()).filter(Boolean);
    if (question.trim().length < 3) { toast.error('La pregunta es muy corta'); return; }
    if (cleanOptions.length < 2) { toast.error('Necesitas al menos 2 opciones'); return; }
    if (audienceEnabled && audienceSelected.size === 0) {
      toast.error('Seleccioná al menos un destinatario o desmarcá la audiencia dirigida');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        question: question.trim(),
        options: cleanOptions,
        show_voters: showVoters,
      };
      if (closesAt) body.closes_at = new Date(closesAt).toISOString();
      if (audienceEnabled && audienceSelected.size > 0) {
        body.target_user_ids = Array.from(audienceSelected);
      }
      const r = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success('📊 Encuesta publicada');
        reset();
        onClose();
        onCreated?.(data);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'No se pudo crear');
      }
    } catch (e) {
      toast.error('Error de red');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const accentBtn = accent === 'indigo'
    ? 'bg-indigo-600 hover:bg-indigo-700'
    : 'bg-purple-600 hover:bg-purple-700';
  const accentIcon = accent === 'indigo' ? 'text-indigo-600' : 'text-purple-600';
  const accentRing = accent === 'indigo' ? 'focus:ring-indigo-500' : 'focus:ring-purple-500';
  const accentText = accent === 'indigo' ? 'text-indigo-600 hover:text-indigo-700' : 'text-purple-600 hover:text-purple-700';
  const accentCheck = accent === 'indigo' ? 'accent-indigo-600' : 'accent-purple-600';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="create-poll-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <BarChart3 className={`w-5 h-5 ${accentIcon}`} />
            {title}
          </h3>
          <button onClick={() => { reset(); onClose(); }} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Pregunta</label>
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              maxLength={280}
              placeholder="¿Qué tema querés que tratemos en la próxima clase?"
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none h-20 focus:ring-2 ${accentRing} focus:border-transparent`}
              data-testid="poll-question-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Opciones (2-6)</label>
            <div className="space-y-2">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-5">{i + 1}.</span>
                  <input
                    type="text"
                    value={opt}
                    onChange={e => updateOption(i, e.target.value)}
                    maxLength={120}
                    placeholder={`Opción ${i + 1}`}
                    className={`flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 ${accentRing} focus:border-transparent`}
                    data-testid={`poll-option-input-${i}`}
                  />
                  {options.length > 2 && (
                    <button onClick={() => removeOption(i)} className="text-gray-400 hover:text-red-500 p-1">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {options.length < 6 && (
              <button onClick={addOption} className={`mt-2 text-xs font-medium flex items-center gap-1 ${accentText}`} data-testid="poll-add-option-btn">
                <Plus className="w-3.5 h-3.5" /> Agregar opción
              </button>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Fecha de cierre (opcional)</label>
            <input
              type="datetime-local"
              value={closesAt}
              onChange={e => setClosesAt(e.target.value)}
              className={`w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 ${accentRing} focus:border-transparent`}
              data-testid="poll-closes-at-input"
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showVoters}
              onChange={e => setShowVoters(e.target.checked)}
              className={`mt-0.5 w-4 h-4 ${accentCheck} cursor-pointer`}
              data-testid="poll-show-voters-checkbox"
            />
            <div className="text-sm text-gray-700">
              <span className="font-medium">Mostrar quién votó qué</span>
              <div className="text-xs text-gray-500">Si está desactivado, los votos son anónimos</div>
            </div>
          </label>

          {/* Audiencia dirigida (opcional) — solo si el host pasó audienceOptions */}
          {Array.isArray(audienceOptions) && audienceOptions.length > 0 && (
            <div className="border-t pt-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={audienceEnabled}
                  onChange={e => setAudienceEnabled(e.target.checked)}
                  className={`mt-0.5 w-4 h-4 ${accentCheck} cursor-pointer`}
                  data-testid="poll-audience-toggle"
                />
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Dirigir solo a personas específicas</span>
                  <div className="text-xs text-gray-500">Si está marcado, solo los seleccionados podrán votar y recibirán notificación. Los demás verán la encuesta pero no podrán votar.</div>
                </div>
              </label>
              {audienceEnabled && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50 space-y-1" data-testid="poll-audience-list">
                  <div className="flex justify-between items-center text-xs px-1 pb-1">
                    <span className="font-semibold text-gray-700">{audienceSelected.size} seleccionado(s)</span>
                    <div className="flex gap-2">
                      <button onClick={() => setAudienceSelected(new Set(audienceOptions.map(u => u.id)))} className="text-blue-600 hover:text-blue-800 font-semibold" data-testid="poll-audience-todos">Todos</button>
                      <span className="text-gray-300">|</span>
                      <button onClick={() => setAudienceSelected(new Set())} className="text-gray-600 hover:text-gray-800 font-semibold" data-testid="poll-audience-ninguno">Ninguno</button>
                    </div>
                  </div>
                  {audienceOptions.map(u => (
                    <label key={u.id} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-white text-sm ${audienceSelected.has(u.id) ? 'bg-blue-50' : ''}`} data-testid={`poll-audience-item-${u.id}`}>
                      <input
                        type="checkbox"
                        checked={audienceSelected.has(u.id)}
                        onChange={() => toggleAudience(u.id)}
                        className="w-4 h-4 accent-blue-600"
                      />
                      <span className="flex-1 truncate">{u.name}</span>
                      {u.role && <span className="text-[10px] text-gray-500">{u.role === 'admin' ? '👑' : u.role === 'profesor' ? '🎓' : ''}</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end gap-2">
          <button onClick={() => { reset(); onClose(); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={submitting || question.trim().length < 3}
            className={`px-4 py-2 text-sm ${accentBtn} disabled:bg-gray-300 text-white rounded-lg flex items-center gap-2`}
            data-testid="poll-submit-btn"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Publicando...</> : <>Publicar encuesta</>}
          </button>
        </div>
      </div>
    </div>
  );
};
